import type { Ref } from "vue";
import type { ToastProps } from "../primitives/toast/Toast";
import { defineComponent, ref } from "vue";
import { cn } from "@/utils/cn";
import Toast from "../primitives/toast/Toast";
import Icon from "../ui/icon/Icon";

type ToastMode = "forbidden" | "success" | "warning" | "error" | "info" | "removed" | "party" | "switch";

export interface ToasterToast extends ToastProps {
  id: string;
  title?: string;
  mode?: ToastMode;
  description?: string;
  descriptionLimit?: number;
  action?: {
    label: string;
    altText?: string;
    handler: (close: () => void) => void | Promise<void>;
  };
}

const icons: Record<
  ToastMode,
  {
    icon: string;
    class: string;
  }
> = {
  forbidden: {
    icon: "lucide:ban",
    class: "bg-indigo-9",
  },
  success: {
    icon: "lucide:check",
    class: "bg-green-9 ",
  },
  warning: {
    icon: "lucide:alert-triangle",
    class: "bg-amber-11 ",
  },
  error: {
    icon: "lucide:x",
    class: "bg-red-9 ",
  },
  info: {
    icon: "lucide:info",
    class: "bg-sky-11 ",
  },
  removed: {
    icon: "lucide:trash-2",
    class: "bg-indigo-9 ",
  },
  party: {
    icon: "twemoji:party-popper",
    class: "p-0 mx-1",
  },
  switch: {
    icon: "twemoji:file-folder",
    class: "p-0 mx-1",
  },
};

const LIMIT = 3;
const DEFAULT_DESCRIPTION_LIMIT = 200;
const toasts = ref<(Omit<ToasterToast, "open"> & { open: Ref<boolean> })[]>([]);

export default defineComponent({
  name: "ToasterProvider",
  setup() {
    return () => (
      <Toast.Provider>
        {toasts.value.map((props) => {
          const { id, title, description, mode, action } = props;

          return (
            <Toast.Root
              {...props}
              key={id}
              v-model:open={props.open}
              duration={props.duration ?? 5000}
              class="flex w-full"
            >
              <div class="center w-full gap-3">
                {mode && (
                  <div
                    class={cn(
                      "center flex shrink-0 rounded-full p-1 text-white",
                      title ? "h-7 w-7" : "h-6 w-6",
                      icons[mode].class
                    )}
                  >
                    <Icon icon={icons[mode].icon} class="h-full w-full" />
                  </div>
                )}
                <div class="grid w-full flex-1 gap-1">
                  {title && <Toast.Title>{title}</Toast.Title>}
                  {description && <Toast.Description>{description}</Toast.Description>}
                </div>
              </div>
              {action && (
                <Toast.Action
                  asChild
                  altText={action.label ?? action.altText}
                  class="ml-6 flex flex-none items-center"
                >
                  <button
                    class="inline-flex h-8 shrink-0 items-center justify-center text-nowrap rounded-md border bg-transparent px-3 text-sm font-medium transition-colors hover:bg-background/10 focus:outline-hidden focus:ring-1 disabled:pointer-events-none disabled:opacity-50"
                    onClick={() => {
                      action.handler(() => removeToast(id));
                    }}
                  >
                    {action.label}
                  </button>
                </Toast.Action>
              )}
              <Toast.Close>
                <Icon icon="lucide:x" />
              </Toast.Close>
            </Toast.Root>
          );
        })}

        <Toast.Viewport />
      </Toast.Provider>
    );
  },
});

function genId() {
  return Math.random().toString(36).slice(2, 11);
}

function truncateDescription(text: string, limit: number): string {
  if (!text || text.length <= limit) return text;
  return text.substring(0, limit - 3) + "...";
}

export function toaster(toast: string | Omit<ToasterToast, "id">) {
  const id = genId();

  if (toasts.value.filter((t) => t.open).length >= LIMIT) {
    toasts.value = toasts.value.slice(1);
  }

  const newToast = typeof toast === "string" ? { description: toast } : toast;

  // Apply description truncation
  if (newToast.description) {
    const limit = newToast.descriptionLimit ?? DEFAULT_DESCRIPTION_LIMIT;
    newToast.description = truncateDescription(newToast.description, limit);
  }

  toasts.value = [
    ...toasts.value,
    {
      id,
      open: false,
      ...newToast,
    },
  ];

  setTimeout(() => {
    const toast = toasts.value.find((toast) => toast.id === id);
    if (!toast) return;

    toast.open = true;
  }, 100);

  return id;
}

const generateVariants = (mode: ToastMode) => {
  return (toast: string | Omit<ToasterToast, "id" | "type" | "open">) => {
    if (typeof toast === "string") {
      return toaster({ description: toast, variant: "default", mode });
    }

    return toaster({
      ...toast,
      variant: "default",
      mode,
    });
  };
};

toaster.success = generateVariants("success");
toaster.warning = generateVariants("warning");
toaster.error = generateVariants("error");
toaster.info = generateVariants("info");
toaster.forbidden = generateVariants("forbidden");
toaster.removed = generateVariants("removed");
toaster.party = generateVariants("party");
toaster.switch = generateVariants("switch");

export function removeToast(id: string, delay = 1000) {
  const toast = toasts.value.find((toast) => toast.id === id);

  if (!toast) return;

  toast.open = false;
  setTimeout(() => removeToast(id, delay), delay);
}
